// TES-1233 (Emelie 2026-10-11, TES-1236 point 1) — the pure half of the "Möten"
// calendar: day arithmetic, the time axis, block layout, the month's counts
// and the short texts a block carries. No React and no path aliases, so
// `node --test` runs it (test/meeting-calendar.test.ts).
//
// Times are the browser's local time, like every other date in the portal;
// the clinic and its doctor are in Sweden.
import {
  clinicianMeetingKind,
  MEETING_KIND_LABEL,
  meetingCalendarStatus,
  type ClinicianMeeting,
  type ClinicianMeetingCalendar,
  type MeetingCalendarStatus,
  type MeetingEvidence,
} from "@care-by-ella/contracts";

const MIN = 60_000;

// ---------------------------------------------------------------------------
// Days
// ---------------------------------------------------------------------------

/** Local midnight of the day `d` falls on. */
export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Calendar days, not 24-hour steps: a DST night has 23 or 25 hours. */
export function addDays(d: Date, days: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + days);
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "2026-10-11" in local time — the URL's day parameter. */
export function isoDay(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}

/** The inverse of isoDay; null for anything that is not a real date. */
export function parseIsoDay(s: string | null | undefined): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s ?? "");
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return isoDay(d) === s ? d : null;
}

/** Emelie: "Rullande sjudagarsvy" — seven days from the anchor day. */
export function weekDays(anchor: Date): Date[] {
  const first = startOfDay(anchor);
  return Array.from({ length: 7 }, (_, i) => addDays(first, i));
}

/** The month view's grid: six Monday-first weeks around the month. */
export function monthGrid(month: Date): Date[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const mondayOffset = (first.getDay() + 6) % 7;
  const start = addDays(first, -mondayOffset);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

// ---------------------------------------------------------------------------
// The time axis
// ---------------------------------------------------------------------------

export const DEFAULT_FIRST_HOUR = 7;
export const DEFAULT_LAST_HOUR = 19;

export function minutesIntoDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * The hours the grid shows: the clinic's ordinary day, widened to fit any
 * window or meeting that falls outside it, so nothing booked is ever off
 * the grid.
 */
export function hourRange(
  intervals: ReadonlyArray<{ start: Date; end: Date }>,
): { firstHour: number; lastHour: number } {
  let first = DEFAULT_FIRST_HOUR;
  let last = DEFAULT_LAST_HOUR;
  for (const { start, end } of intervals) {
    first = Math.min(first, start.getHours());
    const endMinute = sameDay(start, end) ? minutesIntoDay(end) : 24 * 60;
    last = Math.max(last, Math.ceil(endMinute / 60));
  }
  return { firstHour: Math.max(0, first), lastHour: Math.min(24, last) };
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

export interface TimedItem {
  key: string;
  start: Date;
  end: Date;
}

/**
 * Side-by-side columns for items that overlap in time (two doctors in the
 * admin's view, or a cancelled meeting under the one rebooked in its place).
 * Greedy: each item takes the first free column of its overlap cluster, and
 * every item in a cluster shares that cluster's column count.
 */
export function layoutColumns<T extends TimedItem>(
  items: readonly T[],
): Array<T & { column: number; columns: number }> {
  const sorted = [...items].sort(
    (a, b) =>
      a.start.getTime() - b.start.getTime() ||
      b.end.getTime() - a.end.getTime(),
  );
  const out: Array<T & { column: number; columns: number }> = [];
  let cluster: Array<T & { column: number; columns: number }> = [];
  let clusterEnd = -Infinity;
  let columnEnds: number[] = [];
  const flush = () => {
    const columns = Math.max(1, columnEnds.length);
    for (const c of cluster) c.columns = columns;
    out.push(...cluster);
    cluster = [];
    columnEnds = [];
    clusterEnd = -Infinity;
  };
  for (const item of sorted) {
    const s = item.start.getTime();
    // Nothing in the cluster reaches this item: the cluster is complete.
    if (cluster.length > 0 && s >= clusterEnd) flush();
    let column = columnEnds.findIndex((end) => end <= s);
    if (column === -1) {
      column = columnEnds.length;
      columnEnds.push(item.end.getTime());
    } else {
      columnEnds[column] = item.end.getTime();
    }
    cluster.push({ ...item, column, columns: 1 });
    clusterEnd = Math.max(clusterEnd, item.end.getTime());
  }
  if (cluster.length > 0) flush();
  return out;
}

export function meetingEnd(
  m: Pick<ClinicianMeeting, "scheduledAt" | "durationMinutes">,
): Date {
  return new Date(new Date(m.scheduledAt).getTime() + m.durationMinutes * MIN);
}

// ---------------------------------------------------------------------------
// Texts
// ---------------------------------------------------------------------------

export function meetingKindLabel(
  m: Pick<ClinicianMeeting, "reason" | "patientGroup">,
): string {
  return MEETING_KIND_LABEL[clinicianMeetingKind(m)];
}

export function clockTime(d: Date | string): string {
  return new Date(d).toLocaleTimeString("sv-SE", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function duration(ms: number): string {
  const total = Math.max(1, Math.round(ms / MIN));
  const days = Math.floor(total / (24 * 60));
  const hours = Math.floor((total % (24 * 60)) / 60);
  const minutes = total % 60;
  if (days > 0) return hours > 0 ? `${days} d ${hours} h` : `${days} d`;
  if (hours > 0)
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  return `${minutes} min`;
}

/**
 * Emelie: "nedräkning" / "tid till mötet". Proposed wording: "Börjar om
 * 25 min", "Började för 3 min sedan", and "Mötet är slut" once it is over.
 */
export function countdownText(
  m: Parameters<typeof meetingCalendarStatus>[0],
  now: number,
): string {
  const start = new Date(m.scheduledAt).getTime();
  const status = meetingCalendarStatus(m, now);
  if (status === "booked") return `Börjar om ${duration(start - now)}`;
  if (status === "ongoing") {
    return now < start
      ? `Börjar om ${duration(start - now)}`
      : `Började för ${duration(now - start)} sedan`;
  }
  if (status === "cancelled") return "Avbokat";
  return "Mötet är slut";
}

/** "i dag 14:00", "i morgon 09:20" or "tis 14 okt 09:20". */
export function whenText(iso: string, now: Date): string {
  const d = new Date(iso);
  if (sameDay(d, now)) return `i dag ${clockTime(d)}`;
  if (sameDay(d, addDays(now, 1))) return `i morgon ${clockTime(d)}`;
  return `${d.toLocaleDateString("sv-SE", {
    weekday: "short",
    day: "numeric",
    month: "short",
  })} ${clockTime(d)}`;
}

// Emelie: "status för nödvändigt underlag". The wording is ours (proposed);
// "Underlag för receptövertag" is the portal's existing name for F-07.
export function evidenceText(
  e: MeetingEvidence | null | undefined,
): { text: string; missing: boolean } | null {
  if (!e) return null;
  const missing = e.status === "missing";
  switch (e.kind) {
    case "health_declaration":
      return {
        text: missing
          ? "Hälsodeklaration saknas"
          : "Hälsodeklaration inskickad",
        missing,
      };
    case "prescription_transfer":
      return {
        text: missing
          ? "Underlag för receptövertag saknas"
          : "Underlag för receptövertag inskickat",
        missing,
      };
    case "completion":
      return {
        text: missing ? "Underlag saknas" : "Underlag inkommet",
        missing,
      };
  }
}

// ---------------------------------------------------------------------------
// Month counts
// ---------------------------------------------------------------------------

/**
 * Emelie: "antal bokade möten och lediga tider per dag". Booked counts every
 * meeting that still holds its time or has been held — not the cancelled
 * ones, which hold nothing.
 */
export function dayCounts(
  calendar: Pick<ClinicianMeetingCalendar, "meetings" | "freeSlots"> | null,
  day: Date,
): { booked: number; free: number } {
  if (!calendar) return { booked: 0, free: 0 };
  const booked = calendar.meetings.filter(
    (m) => m.status !== "cancelled" && sameDay(new Date(m.scheduledAt), day),
  ).length;
  const free = calendar.freeSlots.filter((s) =>
    sameDay(new Date(s.startAt), day),
  ).length;
  return { booked, free };
}

/** Statuses that ask the doctor to act, for the block's attention mark. */
export const DEVIATION_STATUSES: readonly MeetingCalendarStatus[] = [
  "patient_no_show",
  "needs_rebooking",
];
